import { useState, useEffect } from "react";
import BookingStepper from "./components/BookingStepper";
import Step1V2 from "./Step1V2";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import Step5 from "./Step5";
import { addDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { message, Spin } from "antd";
import { formatDate } from "../admin/ManageHotelBookings";

export interface Room {
  key: string;
  name: string;
  image: string;
  single: number;
  double: number;
  sortOrder?: number;
  soldOut?: boolean;
  limitSingle?: number | null; // Keep limitSingle
  limitDouble?: number | null; // Keep limitDouble
  bookedSingle?: number;
  bookedDouble?: number;
  remainingSingle?: number | null;
  remainingDouble?: number | null;
}

export interface Guest {
  title: string;
  firstName: string;
  lastName: string;
  mobileCode: string;
  mobile: string;
  email: string;
  primary?: boolean;
  companionTitle?: string;
  companionFirstName?: string;
  companionLastName?: string;
  occupancy: "Single" | "Double"; // ← add this
  companion?: {
    companionTitle: string;
    companionFirstName: string;
    companionLastName: string;
  };
}

export const defaultGuest = (): Guest => ({
  title: "Mr",
  firstName: "",
  lastName: "",
  mobileCode: "971",
  mobile: "",
  email: "",
  occupancy: "Single", // ← add default
  companionTitle: "Mr", // ✅ add this
  companionFirstName: "",
  companionLastName: "",
});

const HotelBookingPage: React.FC = () => {
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  const [airportTransfer, setAirportTransfer] = useState<boolean>(false);
  const [paymentOption, setPaymentOption] = useState<string>("Pay by Link");

  const [checkIn, setCheckIn] = useState<Date | null>(new Date(2025, 10, 13));
  const [checkOut, setCheckOut] = useState<Date | null>(new Date(2025, 10, 18));
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [occupancy, setOccupancy] = useState<"Single" | "Double">("Single");
  const [rooms, setRooms] = useState<number>(1);
  const [guests, setGuests] = useState<Guest[]>([defaultGuest()]);

  const [bookingReference, setBookingReference] = useState<string>("");
  const [roomOptions, setRoomOptions] = useState<Room[]>([]);
  const [isLoadingRooms, setIsLoadingRooms] = useState(true);
  const [closeHotelRegistration, setCloseHotelRegistration] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const settingsSnapshot = await getDocs(collection(db, "settings"));
        settingsSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          setCloseHotelRegistration(data.closeHotelRegistration || false);
        });
      } catch (error) {
        console.error("Error fetching hotel registration setting:", error);
      }
    })();
  }, []);

  useEffect(() => {
    setGuests((prev) => {
      const updated = [...prev];
      while (updated.length < rooms) updated.push(defaultGuest());
      const sliced = updated.slice(0, rooms);

      // Auto-assign first guest as primary if there's only one
      if (sliced.length === 1) {
        sliced[0].primary = true;
      } else {
        // If more than one, ensure only one primary
        const hasPrimary = sliced.some((g) => g.primary);
        if (!hasPrimary) {
          sliced[0].primary = true;
        }
      }

      return sliced;
    });
  }, [rooms]);

  useEffect(() => {
    (async () => {
      setIsLoadingRooms(true);
      const snapshot = await getDocs(collection(db, "rooms"));
      const roomData: Room[] = snapshot.docs
        .map((doc) => {
          const data = doc.data();
          const single = data.single ?? 0;
          const double = data.double ?? 0;
          const limitSingle = data.limitSingle ?? null;
          const limitDouble = data.limitDouble ?? null;
          const bookedSingle = data.bookedSingle ?? 0;
          const bookedDouble = data.bookedDouble ?? 0;
          const remainingSingle =
            limitSingle !== null
              ? Math.max(0, limitSingle - bookedSingle)
              : null;
          const remainingDouble =
            limitDouble !== null
              ? Math.max(0, limitDouble - bookedDouble)
              : null;

          const hasLimit = limitSingle !== null || limitDouble !== null;
          // const soldOut =
          //   hasLimit &&
          //   ((remainingSingle !== null && remainingSingle <= 0) &&
          //     (remainingDouble !== null && remainingDouble <= 0));
          const isKingExecutive =
            data.name?.toLowerCase().trim() === "king executive suite";

          const isDeluxeSuite =
            data.name?.toLowerCase().trim() === "king deluxe suite";

          const soldOut =
            isKingExecutive ||
            isDeluxeSuite ||
            (hasLimit &&
              (Number(remainingSingle) <= 0 || Number(remainingDouble) <= 0));

          return {
            key: doc.id,
            name: data.name,
            image: data.image,
            single,
            double,
            sortOrder: data.sortOrder ?? 0,
            soldOut,
            limitSingle, // Include limitSingle
            limitDouble, // Include limitDouble
            bookedSingle,
            bookedDouble,
            remainingSingle,
            remainingDouble,
          };
        })
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

      setRoomOptions(roomData);
      setIsLoadingRooms(false);
    })();
  }, []);

  const nextStep = () =>
    setStep((prev) => Math.min(prev + 1, 5) as 1 | 2 | 3 | 4 | 5);
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1) as 1 | 2);

  const onConfirm = async () => {
    try {
      const now = new Date();
      const formatted = `${now.getFullYear()}${
        now.getMonth() + 1
      }${now.getDate()}${now.getHours()}${now.getMinutes()}`;
      const ref = `ICA-${formatted}`;

      const nights =
        (checkOut!.getTime() - checkIn!.getTime()) / (1000 * 3600 * 24);

      const totalPrice = guests.reduce((sum, guest) => {
        const rate =
          guest.occupancy === "Single"
            ? selectedRoom!.single
            : selectedRoom!.double;
        return sum + rate * nights;
      }, 0);

      const bookingDoc = await addDoc(collection(db, "hotelBookings"), {
        reference: ref,
        checkIn: checkIn!.toLocaleDateString("en-CA"),
        checkOut: checkOut!.toLocaleDateString("en-CA"),
        rooms,
        roomName: selectedRoom!.name,
        totalPrice,
        timestamp: new Date().toISOString(),
        airportTransfer, // Storing airport transfer at the booking level
        paymentOption, // Storing payment option at the booking level
      });

      for (const guest of guests) {
        const guestData: any = {
          title: guest.title,
          firstName: guest.firstName,
          lastName: guest.lastName,
          mobileCode: guest.mobileCode,
          mobile: guest.mobile,
          email: guest.email,
          primary: !!guest.primary,
          occupancy: guest.occupancy,
        };

        // Only add companion data if it exists
        if (guest.companionFirstName || guest.companionLastName) {
          guestData.companion = {
            companionTitle: guest.companionTitle || "",
            companionFirstName: guest.companionFirstName || "",
            companionLastName: guest.companionLastName || "",
          };
        }

        await addDoc(
          collection(db, `hotelBookings/${bookingDoc.id}/guests`),
          guestData
        );
      }

      setBookingReference(ref);
      const primaryGuest = guests.find((g) => g.primary);

      const roomBreakdown = guests.map((g, index) => {
        const rate =
          g.occupancy === "Single"
            ? selectedRoom!.single
            : selectedRoom!.double;
        const amount = rate * nights;
        return {
          roomNumber: `${index + 1}`,
          occupancy: g.occupancy,
          rate: rate.toFixed(2),
          nights: `${nights}`,
          amount: amount.toFixed(2),
        };
      });

      const enrichedGuests = guests.map((g, index) => {
        const companionFilled = g.companionFirstName || g.companionLastName;
        const base = {
          title: g.title,
          firstName: g.firstName,
          lastName: g.lastName,
          email: g.email,
          mobile: g.mobile,
          isPrimary: !!g.primary,
          occupancy: g.occupancy,
          roomNumber: index + 1,
          roomTotalRooms: guests.length,
        };

        return companionFilled
          ? {
              ...base,
              companion: {
                companionTitle: g.companionTitle || "",
                companionFirstName: g.companionFirstName || "",
                companionLastName: g.companionLastName || "",
              },
            }
          : base;
      });

      const dynamicData = {
        fullName:
          `${primaryGuest?.title} ${primaryGuest?.firstName} ${primaryGuest?.lastName}`.trim(),
        email: primaryGuest?.email,
        mobile: `+${primaryGuest?.mobileCode} ${primaryGuest?.mobile}`,

        checkIn: checkIn ? formatDate(checkIn) : "",
        checkOut: checkOut ? formatDate(checkOut) : "",

        roomName: selectedRoom?.name,
        // ratePerRoom: pricePerRoom.toFixed(2),
        totalRooms: rooms,
        totalNights: nights,
        totalPrice: (totalPrice + (airportTransfer ? 130 : 0)).toFixed(2),
        roomBreakdown,
        guests: enrichedGuests,
        bookingReference: ref,
        invoiceDate: new Date().toLocaleString(),
        paymentOption,
        airportTransfer: airportTransfer ? "Yes" : "",
      };

      await fetch(
        "https://us-central1-icaconference-438ba.cloudfunctions.net/sendHotelConfirmation",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            personalizations: [
              {
                to: [{ email: primaryGuest?.email }],
                dynamic_template_data: dynamicData,
              },
            ],
            from: {
              email: "registration@comms-dev.com",
              name: "ICA Conference",
            },
            subject: "ICA Hotel Booking Confirmation",
            template_id: "d-c4736ba558274a489f155777f0d65986",
          }),
        }
      );

      setStep(5);
    } catch (err: any) {
      console.error("Booking failed:", err);
      message.error("Failed to confirm reservation. Please try again.");
    }
  };

  const renderCurrentStep = () => {
    switch (step) {
      case 1:
        return (
          <Step1V2
            onNext={nextStep}
            checkIn={checkIn}
            checkOut={checkOut}
            setCheckIn={setCheckIn}
            setCheckOut={setCheckOut}
            selectedRoom={selectedRoom}
            setSelectedRoom={setSelectedRoom}
            roomOptions={roomOptions}
            isLoadingRooms={isLoadingRooms}
          />
        );
      case 2:
        return (
          <Step2
            checkIn={checkIn!}
            checkOut={checkOut!}
            selectedRoom={selectedRoom!}
            occupancy={occupancy}
            setOccupancy={setOccupancy}
            rooms={rooms}
            setRooms={setRooms}
            guests={guests}
            setGuests={setGuests}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 3:
        return (
          <Step3
            checkIn={checkIn!}
            checkOut={checkOut!}
            selectedRoom={selectedRoom!}
            occupancy={occupancy}
            setOccupancy={setOccupancy}
            rooms={rooms}
            setRooms={setRooms}
            guests={guests}
            setGuests={setGuests}
            airportTransfer={airportTransfer} // Pass airportTransfer to Step3
            setAirportTransfer={setAirportTransfer} // Pass setter for airportTransfer
            paymentOption={paymentOption} // Pass paymentOption to Step3
            setPaymentOption={setPaymentOption} // Pass setter for paymentOption
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 4:
        return (
          <Step4
            checkIn={checkIn!}
            checkOut={checkOut!}
            selectedRoom={selectedRoom!}
            occupancy={occupancy}
            rooms={rooms}
            guests={guests}
            onBack={prevStep}
            onConfirm={onConfirm}
            airportTransfer={airportTransfer}
            paymentOption={paymentOption}
          />
        );
      case 5:
        return <Step5 bookingReference={bookingReference} amount={0} />;
      default:
        return null;
    }
  };

  return (
    <section id="partners" className="pt-50">
      <div className="min-h-screen bg-gray-50">
        {closeHotelRegistration ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "80px 20px",
              minHeight: "60vh",
              backgroundColor: "#fff",
            }}
          >
            <div style={{ textAlign: "center", maxWidth: 600 }}>
              <h1
                style={{
                  color: "#000",
                  fontWeight: 700,
                  fontSize: 28,
                  marginBottom: 16,
                  letterSpacing: "-0.02em",
                }}
              >
                HOTEL REGISTRATION IS NOW CLOSED
              </h1>

              <p
                style={{
                  fontSize: 15,
                  color: "#444",
                  lineHeight: 1.7,
                  marginBottom: 24,
                }}
              >
                Thank you for your interest. Our official hotel booking for
                conference delegates is now closed. <br />
                For any assistance or inquiries, you may contact us at:
              </p>

              <p
                style={{
                  color: "#D39E2D",
                  fontWeight: 600,
                  fontSize: 16,
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                +971 4 256 6622 &nbsp; | &nbsp; +971 55 258 9011
                <br />
                <a
                  href="mailto:eman@uaefma.com"
                  style={{
                    color: "#D39E2D",
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  eman@uaefma.com
                </a>
              </p>
            </div>
          </div>
        ) : (
          <>
            <BookingStepper currentStep={step} />
            {renderCurrentStep()}
          </>
        )}
      </div>
    </section>
  );
};

export default HotelBookingPage;
